import React, { useState, useRef, useEffect, useCallback } from 'react'
import './AudioPlayer.css';
// NORTON DESIGN SYSTEM
import { Button, Tooltip, Popover, Dropdown } from '@wwnds/react';
// COMPONENTS
import AudioSlider from '../AudioSlider/AudioSlider';
import VolumeSlider from '../VolumeSlider/VolumeSlider';
import AudioSliderTooltip from '../AudioSliderTooltip/AudioSliderTooltip';

const AudioPlayer = ({
  //PROPS
  audRef,
  isPlaying,
  setIsPlaying,
  setCurrentTime,
  dataLoaded,
  Data,
  timelineIndex,
  formatSectionHeaderStr,
  updateTime,
  formatTime }) => {
  // REFS
  const audioSliderRef = useRef(null)
  // HOOKS
  const [sliderVal, setSliderVal] = useState(0)
  const [timeStamp, setTimeStamp] = useState("0:00 / 0:00")
  const [tooltipLocation, setTooltipLocation] = useState(0)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipLabel, setTooltipLabel] = useState("")
  const [tooltipPointerLocation, setTooltipPointerLocation] = useState(0)
  const [volumeOpen, setVolumeOpen] = useState(false)
  const [volumeBtnRef, setVolumeBtnRef] = useState()
  const [volume, setVolume] = useState(50)

  // EFFECTS
  useEffect(() => {
    const updatedTimePercent = (audRef.current.currentTime / audRef.current.duration) * 100
    setSliderVal(updatedTimePercent)
    if (audRef.current && dataLoaded) {
      setTimeStamp(<div className="timeStampContainer"><span className="time-stamp-current">{formatTime(audRef.current.currentTime)}</span><span>{formatTime(audRef.current.duration)}</span></div>)
    }
  }, [dataLoaded, audRef, formatTime])
  // set volume to 50% when audio track loads
  useEffect(() => {
    if (dataLoaded && audRef.current) {
      audRef.current.volume = 0.5
    }
  }, [audRef, dataLoaded])

  // EVENT HANDLERS
  // plays or pauses the audio track
  const handlePlayBtnClick = useCallback((e) => {
    if (!isPlaying) {
      audRef.current.play()
      setIsPlaying(true)
    } else {
      audRef.current.pause()
      setIsPlaying(false)
    }
  }, [audRef, isPlaying, setIsPlaying])
  // helper function for rewind/fast-forward
  const jumpTime = useCallback((seconds) => {
    audRef.current.currentTime = audRef.current.currentTime + seconds
  }, [audRef])
  // rewind/fast-forward event handlers
  const handleRewind = () => { jumpTime(-10) }
  const handleFastForward = () => { jumpTime(10) }
  // handles scrubbing of audio slider
  const handleScrub = (curVal) => {
    setSliderVal(curVal)
    const duration = audRef.current.duration
    const curValPercent = curVal / 100
    const updatedTime = (curValPercent * duration)
    updateTime(updatedTime)
    audRef.current.currentTime = updatedTime
  }
  // handles tickmark click
  const handleTickmarkClick = (timeStart) => {
    audRef.current.currentTime = timeStart
    setCurrentTime(timeStart)
  }
  // shows tooltip when mouse hovers over audio slider
  const handleHover = (e) => {
    let hoverPosition
    // if hovering over tickmark, set position at midpoint of tickmark button
    if (e.target.localName === "button" || e.target.localName === "span") {
      hoverPosition = (e.target.offsetWidth / 2) + e.target.getBoundingClientRect().left - 3
    } else {
      hoverPosition = e.pageX
    }
    // get starting and ending positions of the parent
    const boundaryLeft = audioSliderRef.current.getBoundingClientRect().left
    const boundaryRight = audioSliderRef.current.getBoundingClientRect().right
    // calculate tooltip position relative to parent
    const position = e.pageX - boundaryLeft
    let hoverPositionNum = parseFloat(hoverPosition)
    // calculate pointer position, limiting its range so that it stays on top of the parent (track)
    const pointerPosition = hoverPositionNum - 6 < boundaryLeft ? boundaryLeft - 3 : hoverPositionNum > boundaryRight - 13 ? boundaryRight - 13 : hoverPositionNum - 6
    setTooltipPointerLocation(pointerPosition - boundaryLeft)
    const correctedPos = getCorrectedLeftPosition(hoverPositionNum)
    setTooltipLocation(correctedPos)
    const posInTimeline = convertPositionToTime(position)
    retrieveSectionFromTime(posInTimeline)
  }
  // shows tooltip when touch hovers over audio slider
  const handleTouchHover = (e) => {
    const touch = e.touches[0]
    const boundaryLeft = audioSliderRef.current.getBoundingClientRect().left
    const boundaryRight = audioSliderRef.current.getBoundingClientRect().right
    const position = touch.pageX - boundaryLeft
    let hoverPositionNum = parseFloat(touch.pageX)
    const pointerPosition = hoverPositionNum - 6 < boundaryLeft ? boundaryLeft - 3 : hoverPositionNum > boundaryRight - 13 ? boundaryRight - 13 : hoverPositionNum - 6
    setTooltipPointerLocation(pointerPosition - boundaryLeft)
    const correctedPos = getCorrectedLeftPosition(hoverPositionNum)
    setTooltipLocation(correctedPos)
    const posInTimeline = convertPositionToTime(position)
    retrieveSectionFromTime(posInTimeline)
  }
  // handle change in volume slider
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume)
    audRef.current.volume = newVolume / 100
  }

  // UTILS
  const getCorrectedLeftPosition = (tooltipLocation) => {
    if (audioSliderRef.current) {
      if (parseInt(tooltipLocation) <= 150) {
        return 0
      } else if (parseInt(tooltipLocation) > (audioSliderRef.current.offsetWidth - 105)) {
        return (audioSliderRef.current.offsetWidth - 255)
      } else {
        return (tooltipLocation - 150)
      }
    } else {
      return tooltipLocation
    }
  }
  const convertPositionToTime = (position) => {
    if (audioSliderRef.current) {
      const totalWidth = audioSliderRef.current.offsetWidth
      const positionPercent = position / totalWidth
      const duration = audRef.current ? audRef.current.duration : 0
      const time = positionPercent * duration
      return time
    }
  }
  const convertTimeToPosition = (time) => {
    if (audioSliderRef.current) {
      const duration = audRef.current ? audRef.current.duration : 0
      const timePercent = time / duration
      const totalWidth = audioSliderRef.current.offsetWidth
      const position = timePercent * totalWidth
      return position
    }
  }
  const retrieveSectionFromTime = (time) => {
    Data.enableMod.filter(mod => mod.type === "interact")[0].interact.timelines.forEach((timeline, index) => {
      if (timeline.frames[0].in < time && time < timeline.frames[0].out) {
        setTooltipLabel(<p className="tooltip-label-container"><span className="tooltip-label-span tooltip-label-span-timestamp">[{formatTime(timeline.frames[0].in)}–{formatTime(timeline.frames[0].out)}]</span><span className="tooltip-label-span" dangerouslySetInnerHTML={{ __html: formatSectionHeaderStr(timeline.timeline) }}></span></p>)
      }
    })
  }
  // KEYBOARD SHORTCUTS
  // Keyboard shortcut helper function
  const handleKeyPress = useCallback((event) => {
    if (event.altKey) {
      if (event.keyCode === 74) {
        jumpTime(-10)
      } else if (event.keyCode === 75) {
        handlePlayBtnClick()
      } else if (event.keyCode === 76) {
        jumpTime(10)
      }
    }
  }, [jumpTime, handlePlayBtnClick]);
  // Keyboard press listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);
  // TOOLTIPS
  // Tooltip references
  const [rewindBtnRef, setRewindBtnRef] = useState(null)
  const [forwardBtnRef, setForwardBtnRef] = useState(null)
  const [playBtnRef, setPlayBtnRef] = useState(null)
  const [showBtnTooltip, setShowBtnTooltip] = useState({
    rewind: false,
    play: false,
    forward: false
  })
  // Tooltip event handler
  const handleTooltipHover = (e, bool) => {
    const size = window.innerWidth
    const show = size < 800 ? false : bool
    setShowBtnTooltip(prevState => ({ ...prevState, [e.target.name]: show }))
  }

  const sliderThumb = useRef()

  return (
    <div className="audio-player-container">
      <AudioSliderTooltip
        audioSliderRef={audioSliderRef}
        tooltipLocation={tooltipLocation}
        showTooltip={showTooltip}
        tooltipLabel={tooltipLabel}
      />
      <span className="tooltip-pointer" style={{ position: "absolute", left: tooltipPointerLocation || 0, top: "-32px", display: showTooltip ? 'block' : 'none' }}>▾</span>
      <div className="tickmark-btn-container"
        onClick={(e) => handleScrub(((e.pageX - audioSliderRef.current.getBoundingClientRect().left) / audioSliderRef.current.offsetWidth) * 100)}
        onPointerOver={(e) => {
          setShowTooltip(true)
          handleHover(e)
        }
        }
        onPointerMove={(e) => {
          setShowTooltip(true)
          handleHover(e)
        }
        }
        onPointerEnter={() => setShowTooltip(true)}
        onPointerLeave={() => setShowTooltip(false)}
      >
        {Data.enableMod.filter(mod => mod.type === "interact")[0].interact.timelines.map((timeline, index) => (
          <Button
            tabIndex={-1}
            // key={index}
            aria-hidden="true"
            className={index === timelineIndex ? "tickmark-btn tickmark-btn-active" : "tickmark-btn"}
            textClass="tickmark-btn-text"
            style={{ left: (convertTimeToPosition(timeline.frames[0].in) - ((index * 0) + 0)) || 0 }}
            onClick={() => handleTickmarkClick(timeline.frames[0].in)}
            onPointerMove={handleHover}
            onPointerEnter={() => setShowTooltip(true)}
            onPointerLeave={() => setShowTooltip(false)}
          >
            {formatTime(timeline.frames[0].in)} {timeline.timeline}
          </Button>
        ))
        }
      </div>
      <AudioSlider
        value={sliderVal || 0}
        handleScrub={handleScrub}
        onHover={handleHover}
        onPointerEnter={() => setShowTooltip(true)}
        onPointerLeave={() => setShowTooltip(false)}
        reference={audioSliderRef}
        setShowTooltip={setShowTooltip}
        audRef={audRef}
        handleTouchHover={handleTouchHover}
        currentTime={audRef.current ? audRef.current.currentTime : 0}
        duration={audRef.current ? audRef.current.duration : 0}
      />
      <span>{timeStamp}</span>
      <div className="audio-ctrl-container">
        <div className="audio-ctrl-container-left"></div>
        <div className="audio-ctrl-container-center">
          <Button className="audio-ctrl-btn-center" onClick={handleRewind}
            ref={setRewindBtnRef}
            aria-keyshortcuts="Alt+J"
            aria-label="Rewind 10 seconds"
            name="rewind"
            onMouseEnter={(e) => handleTooltipHover(e, true)}
            onMouseLeave={(e) => handleTooltipHover(e, false)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              fast_rewind
            </span>
            <span>
              Rewind
            </span>
          </Button>
          <Tooltip
            isOpen={showBtnTooltip.rewind}
            placement="top"
            reference={rewindBtnRef}
            // trigger="mouseenter"
            trigger="manual"
            bodyClass="audio-ctrl-btn-tooltip-body"
          >
            <kbd>Alt + J</kbd>
          </Tooltip>
          <Button
            ref={setPlayBtnRef}
            aria-keyshortcuts="Alt+K"
            aria-label="Forward 10 seconds"
            className={!isPlaying ? "audio-ctrl-btn-center play-btn" : "audio-ctrl-btn-center pause-btn"} onClick={handlePlayBtnClick}
            name="play"
            onMouseEnter={(e) => handleTooltipHover(e, true)}
            onMouseLeave={(e) => handleTooltipHover(e, false)}
          >
            <span className={"material-symbols-outlined"}>
              {!isPlaying ? "play_arrow" : "pause"}
            </span>
            <span> {!isPlaying ? "Play" : "Pause"}</span>
          </Button>
          <Tooltip
            isOpen={showBtnTooltip.play}
            placement="top"
            reference={playBtnRef}
            trigger="manual"
            bodyClass="audio-ctrl-btn-tooltip-body"
          >
            <kbd>Alt + K</kbd>
          </Tooltip>
          <Button className="audio-ctrl-btn-center" onClick={handleFastForward}
            ref={setForwardBtnRef}
            aria-keyshortcuts="Alt+L"
            name="forward"
            onMouseEnter={(e) => handleTooltipHover(e, true)}
            onMouseLeave={(e) => handleTooltipHover(e, false)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              fast_forward
            </span>
            <span>
              Forward
            </span>
          </Button>
          <Tooltip
            isOpen={showBtnTooltip.forward}
            placement="top"
            reference={forwardBtnRef}
            // trigger="mouseenter"
            trigger="manual"
            bodyClass="audio-ctrl-btn-tooltip-body"
          >
            <kbd>Alt + L</kbd>
          </Tooltip>
        </div>
        <div className="audio-ctrl-container-right">
          {/* <Button className="audio-ctrl-btn" ref={setSectionsBtnRef}>
            <span className="material-symbols-outlined" aria-hidden="true">
              list
            </span>
            <span>
              Sections
            </span>
          </Button> */}
          <Dropdown
            // baseName='nds-dropdown'
            label="Choose your section"
            labelClass='sections-dropdow-label'
            buttonClass='audio-ctrl-btn audio-ctrl-btn-dropdown'
            buttonContents={
              <>
                <span className="material-symbols-outlined" aria-hidden="true" style={{ width: "100%" }}>list</span>
                <span>Sections</span>
              </>}
            onChange={(e) => { updateTime(e.contents.props.id) }}
          >
            {Data.enableMod.filter(mod => mod.type === "interact")[0].interact.timelines.map((timeline, index) => (
              <Dropdown.Option key={`dropdown-option-${index}`}
              // onClick={() => updateTime(timeline.frames[0].in)}
              >
                <div id={timeline.frames[0].in} >
                  <div className="popover-section-label-container-default" id={timeline.frames[0].in}>
                    <span className="sections-btn-for-mobile">
                      <span className="material-symbols-outlined" aria-hidden="true">list</span><span>Sections</span>
                    </span>
                    <span className="timeline-str" dangerouslySetInnerHTML={{ __html: formatSectionHeaderStr(timeline.timeline) }}></span>
                  </div>
                  <div className="popover-section-container"
                    // onClick={() => updateTime(timeline.frames[0].in)}
                    id={timeline.frames[0].in}>
                    <div className={index === timelineIndex ? "highlight-annotation-popover" : "hide-highlight-annotation-popover"}>
                      &#11044;
                    </div>
                    <div className={index === timelineIndex ? "popover-section-label-container popover-section-label-container-highlighted" : "popover-section-label-container"}>
                      <span>[{formatTime(timeline.frames[0].in)}] </span>
                      <span className="popover-section-title" dangerouslySetInnerHTML={{ __html: formatSectionHeaderStr(timeline.timeline) }}></span>
                    </div>
                  </div>
                </div>
              </Dropdown.Option>
            ))}
          </Dropdown>
          <Button className="audio-ctrl-btn" ref={setVolumeBtnRef} onKeyDown={() => { if (sliderThumb && sliderThumb.current) { sliderThumb.current.focus() } }} onClick={() => { if (sliderThumb && sliderThumb.current) { sliderThumb.current.focus() } }}>
            <span className="material-symbols-outlined" aria-hidden="true">
              volume_up
            </span>
            <span>
              Volume
            </span>
          </Button>
          <Popover
            // title="Title"
            bodyClass="popover-volume"
            arrowElement="popover-arrow"
            hideTitle={false}
            hideCloseButton={true}
            placement="bottom-end"
            distance={0}
            reference={volumeBtnRef}
            isOpen={volumeOpen}
            onRequestClose={() => setVolumeOpen(false)}
            onRequestOpen={() => setVolumeOpen(true)}
          >
            <VolumeSlider
              value={volume}
              handleChange={handleVolumeChange}
              volumeOpen={volumeOpen}
              volumeBtnRef={volumeBtnRef}
              sliderThumb={sliderThumb}
              setVolumeOpen={setVolumeOpen}
            />
          </Popover>
        </div>
      </div>
    </div>
  )
}

export default AudioPlayer